import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import HelpLayout from "./HelpLayout";
import sections from "./sections";

function OverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedSlug = searchParams.get("section");
  const sectionRefs = useRef({});

  // The intro topic starts expanded so the page leads with why the site
  // exists, rather than a wall of collapsed headers - unless a specific
  // topic was linked to via ?section=<slug>, in which case that one opens
  // (and gets scrolled into view) instead.
  const [openSlugs, setOpenSlugs] = useState(() => {
    const initial = sections.some((s) => s.slug === linkedSlug)
      ? linkedSlug
      : sections[0]?.slug;
    return new Set(initial ? [initial] : []);
  });

  // Opens and scrolls to whichever section is linked via ?section=<slug> -
  // both on initial load and when a link elsewhere on this page points to
  // a different section while already here.
  useEffect(() => {
    if (!linkedSlug || !sections.some((s) => s.slug === linkedSlug)) return;
    setOpenSlugs((prev) => (prev.has(linkedSlug) ? prev : new Set(prev).add(linkedSlug)));
    sectionRefs.current[linkedSlug]?.scrollIntoView({ block: "start" });
  }, [linkedSlug]);

  const toggle = (slug) => {
    setOpenSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
    setSearchParams(
      (prev) => {
        const isOpening = !openSlugs.has(slug);
        const next = new URLSearchParams(prev);
        if (isOpening) next.set("section", slug);
        else if (next.get("section") === slug) next.delete("section");
        return next;
      },
      { replace: true }
    );
  };

  return (
    <HelpLayout title="Feature Overview" backTo="/help" backLabel="Back to Help">
      <p className="text-sm text-gray-700 mb-4">
        An overview of JokeLab's features. Click a topic to expand it.
      </p>
      <div className="flex flex-col gap-3">
        {sections.map((section) => {
          const open = openSlugs.has(section.slug);
          return (
            <div
              key={section.slug}
              ref={(el) => { sectionRefs.current[section.slug] = el; }}
              className="bg-white rounded-lg shadow-sm overflow-hidden scroll-mt-4"
            >
              <button
                type="button"
                onClick={() => toggle(section.slug)}
                className="w-full flex items-center justify-between text-left p-4 hover:bg-gray-50 transition"
                aria-expanded={open}
              >
                <div>
                  <div className="text-base font-bold text-green-900">{section.title}</div>
                  <div className="text-sm text-gray-600 mt-1">{section.summary}</div>
                </div>
                {open ? (
                  <FiChevronDown size={18} className="text-gray-400 flex-shrink-0 ml-3" />
                ) : (
                  <FiChevronRight size={18} className="text-gray-400 flex-shrink-0 ml-3" />
                )}
              </button>
              {open && (
                <div className="px-4 pb-4 pt-3 border-t border-gray-100 text-sm text-gray-700 leading-relaxed flex flex-col gap-2">
                  {section.content}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </HelpLayout>
  );
}

export default OverviewPage;
